// This is based on https://github.com/react-keycloak/react-keycloak,
// but we had to rewrite it due to lack of activity in the source repo.
import { IAuthContextProps, reactKeycloakWebContext } from './context';

import * as React from 'react';
import isEqual from 'react-fast-compare';
import { AuthClient, AuthClientError, AuthClientEvent, AuthClientInitOptions, AuthClientTokens } from './types';

const UPDATE_TOKEN_TIMEOUT_MS = 15000;

/**
 * Wraps AuthClient#updateToken so a missing refresh token resolves instead of
 * rejecting with `undefined`, and a stalled request can't hang the caller forever
 * (keycloak-js's refreshQueue coalesces concurrent calls behind one request with
 * no timeout of its own).
 */
function updateTokenSafely<T extends AuthClient>(authClient: T, minValidity: number): Promise<boolean> {
  if (!authClient.refreshToken) {
    return Promise.resolve(false);
  }

  return Promise.race([
    authClient.updateToken(minValidity),
    new Promise<boolean>((_, reject) =>
      setTimeout(() => reject(new Error('updateToken timed out')), UPDATE_TOKEN_TIMEOUT_MS),
    ),
  ]);
}

/**
 * Props that can be passed to AuthProvider
 */
export type AuthProviderProps<T extends AuthClient> = {
  /**
   * The single AuthClient instance to be used by your application.
   */
  authClient: T;

  /**
   * A flag to enable automatic token refresh. Defaults to true.
   * This is useful if you need to disable it (not recommended).
   *
   * @default true
   */
  autoRefreshToken?: boolean;

  /**
   * The config to be used when initializing AuthClient instance.
   */
  initOptions?: AuthClientInitOptions;

  /**
   * An optional loading check function to customize LoadingComponent display condition.
   * Return `true` to display LoadingComponent, `false` to hide it.
   *
   * @param authClient the current AuthClient instance.
   *
   * @returns {boolean} Set to true to display LoadingComponent, false to hide it.
   */
  isLoadingCheck?: (authClient: T) => boolean;

  /**
   * An optional component to display while AuthClient instance is being initialized.
   */
  LoadingComponent?: JSX.Element;

  /**
   * An optional function to receive AuthClient events as they happen.
   */
  onEvent?: (eventType: AuthClientEvent, error?: AuthClientError) => void;

  /**
   * An optional function to receive AuthClient tokens when changed.
   *
   * @param {AuthClientTokens} tokens The current AuthClient tokens set.
   */
  onTokens?: (tokens: AuthClientTokens) => void;
};

type AuthProviderState = {
  initialized: boolean;

  isAuthenticated: boolean;

  isLoading: boolean;
};

/**
 * Create an AuthProvider component to wrap a React app with, it will take care of common AuthClient
 * lifecycle handling (such as initialization and token refresh).
 *
 * @param AuthContext the Auth context to be used by the created AuthProvider
 *
 * @returns the AuthProvider component
 */
export function createAuthProvider<T extends AuthClient>(AuthContext: React.Context<IAuthContextProps<T>>) {
  const defaultInitOptions: AuthClientInitOptions = {
    onLoad: 'check-sso',
  };

  const initialState: AuthProviderState = {
    initialized: false,
    isAuthenticated: false,
    isLoading: true,
  };

  return class KeycloakProvider extends React.PureComponent<AuthProviderProps<T>, AuthProviderState> {
    state = {
      ...initialState,
    };

    private tokenRefreshInterval?: number; // Hinzufügen einer Property zum Speichern der Intervall-ID

    componentDidMount() {
      this.init();
      this.startTokenRefresh(); // Starten des Intervalls nach der Initialisierung
    }

    componentDidUpdate({ authClient: prevAuthClient, initOptions: prevInitOptions }: AuthProviderProps<T>) {
      const { initOptions, authClient } = this.props;
      if (authClient !== prevAuthClient || !isEqual(initOptions, prevInitOptions)) {
        // De-init previous AuthClient instance
        prevAuthClient.onReady = undefined;
        prevAuthClient.onAuthSuccess = undefined;
        prevAuthClient.onAuthError = undefined;
        prevAuthClient.onAuthRefreshSuccess = undefined;
        prevAuthClient.onAuthRefreshError = undefined;
        prevAuthClient.onAuthLogout = undefined;
        prevAuthClient.onTokenExpired = undefined;

        // Reset state
        this.setState({ ...initialState });
        // Init new AuthClient instance
        this.init();
        this.stopTokenRefresh(); // Beenden des alten Intervalls
        this.startTokenRefresh(); // Starten des neuen Intervalls
      }
    }

    componentWillUnmount() {
      this.stopTokenRefresh(); // Bereinigen des Intervalls beim Unmounten der Komponente
    }

    init() {
      const { initOptions, authClient } = this.props;
      // Attach Keycloak listeners
      authClient.onReady = this.updateState('onReady');
      authClient.onAuthSuccess = this.updateState('onAuthSuccess');
      authClient.onAuthError = this.onError('onAuthError');
      authClient.onAuthRefreshSuccess = this.updateState('onAuthRefreshSuccess');
      authClient.onAuthRefreshError = this.onError('onAuthRefreshError');
      authClient.onAuthLogout = this.updateState('onAuthLogout');
      authClient.onTokenExpired = this.refreshToken('onTokenExpired');

      authClient.init({ ...defaultInitOptions, ...initOptions }).catch(this.onError('onInitError'));
    }

    startTokenRefresh() {
      const { autoRefreshToken, authClient } = this.props;
      if (autoRefreshToken !== false && !this.tokenRefreshInterval) {
        // Richten Sie ein Intervall ein, das updateToken regelmäßig aufruft
        this.tokenRefreshInterval = window.setInterval(() => {
          // Aktualisieren, wenn weniger als 4 Minuten übrig sind
          updateTokenSafely(authClient, 4 * 60).catch((error) => {
            console.error('Token refresh failed:', error);
            this.onError('onAuthRefreshError')(error); // Behandeln von Fehlern bei der Aktualisierung
          });
        }, 30 * 1000); // Rufen Sie alle 30 Sekunden auf (passen Sie dies bei Bedarf an)
      }
    }

    stopTokenRefresh() {
      if (this.tokenRefreshInterval) {
        clearInterval(this.tokenRefreshInterval);
        this.tokenRefreshInterval = undefined;
      }
    }

    onError = (event: AuthClientEvent) => (error?: AuthClientError) => {
      const { onEvent } = this.props;
      // Notify Events listener
      onEvent && onEvent(event, error);
    };

    updateState = (event: AuthClientEvent) => () => {
      const { authClient, onEvent, onTokens, isLoadingCheck } = this.props;
      const { initialized: prevInitialized, isAuthenticated: prevAuthenticated, isLoading: prevLoading } = this.state;
      // Notify Events listener
      onEvent && onEvent(event);

      // Check Loading state
      const isLoading = isLoadingCheck ? isLoadingCheck(authClient) : false;

      // Check if user is authenticated
      const isAuthenticated = isUserAuthenticated(authClient);

      // Avoid double-refresh if state hasn't changed
      if (!prevInitialized || isAuthenticated !== prevAuthenticated || isLoading !== prevLoading) {
        this.setState({
          initialized: true,
          isAuthenticated,
          isLoading,
        });
      }

      // Notify token listener, if any
      const { idToken, refreshToken, token } = authClient;
      onTokens &&
        onTokens({
          idToken,
          refreshToken,
          token,
        });
    };

    // Fallback for when keycloak-js's own access-token-expiry timer fires (e.g. the
    // 30s interval above missed its window, such as after a backgrounded tab).
    refreshToken = (event: AuthClientEvent) => () => {
      const { authClient, onEvent } = this.props;
      // Notify Events listener
      onEvent && onEvent(event);

      updateTokenSafely(authClient, 5).catch((error) => {
        console.error('Token refresh failed:', error);
        this.onError('onAuthRefreshError')(error);
      });
    };

    render() {
      // @ts-ignore
      const { children, authClient, LoadingComponent } = this.props;
      const { initialized, isLoading } = this.state;

      if (!!LoadingComponent && (!initialized || isLoading)) {
        return LoadingComponent;
      }

      return <AuthContext.Provider value={{ initialized, authClient }}>{children}</AuthContext.Provider>;
    }
  };
}

function isUserAuthenticated(authClient: AuthClient) {
  return !!authClient.idToken && !!authClient.token;
}

export const ReactKeycloakProvider = createAuthProvider(reactKeycloakWebContext) as any;
